import { useLazyQuery } from "@apollo/client";
import { Grid, Switch, useMediaQuery, withStyles } from "@material-ui/core";
import { DeleteDialog } from "components/DeleteDialog";
import NavSwitcher, { TabProps } from "components/NavSwitcher";
import SearchBox from "components/SearchBox";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import { COLORS } from "constants/colors";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { JobContext } from "contexts/JobContext";
import { useOrderActions, useParcelActions } from "domains/Parcel/data";
import NewReceiver from "domains/Receiver/Components/NewReceiver";
import UpdateReceiver from "domains/Receiver/Components/UpdateReceiver";
import ParcelsExportFormModal from "domains/Task/components/HomeSides/ParcelsExportFormModal";
import { dateContext } from "domains/Task/components/HomeSides/Recap/context/dateContext";
import { HeaderParcelTracking } from "domains/Task/components/HomeSides/Recap/HeaderSection";
import { RecapParcels } from "domains/Task/graphql/queries";
import { useOrdersColums, useParcelRowColumns } from "domains/Task/types/Column";
import { Order, OrderStatus, Parcel } from "generated/graphql";
import { useCurrentClient, useCurrentUser } from "hooks";
import { useCountryCode } from "hooks/useCurrentUser";
import moment from "moment";
import React from "react";
import "styled-components/macro";
import ActionsGroup from "ui/ActionGroup/actiongroup";
import { generateAirwayBills } from "utils";
import DeleteOrder from "../Orders/DeleteOrder";
import DeleteParcel from "./DeleteParcel";
import NewParcel from "./NewParcel";
import { useParcelStyles } from "./styles";
import UpdateParcel from "./UpdateParcel";

const useTabs = (onprogressCount = 0, successfulCount = 0, cancelledCount = 0): TabProps[] => {
  const { text } = useText();
  return [
    {
      libelle: text.status.in_progress,
      id: "onprogress",
      count: onprogressCount,
    },
    {
      libelle: text.status.successful,
      id: "successful",
      count: successfulCount,
    },

    {
      libelle: text.status.cancelled,
      id: "cancelled",
      count: cancelledCount,
    },
  ];
};

const AntSwitch = withStyles({
  switchBase: {
    color: theme.palette.primary.main,
    "&$checked": {
      color: theme.palette.primary.main,
    },
    "&$checked + $track": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  checked: {},
  track: {},
})(Switch);
export interface ParcelTrackingListProps {
  actionSwitcher: (tab: TabProps) => void;
  parcels?: { data: Parcel[]; loading: boolean };
  switcher?: (value: boolean) => void;
  ordersData?: { orders: Order[]; orderLoading: boolean };
  onClickOrder?: (order: Order) => void;
  onClickParcel?: (parcel: Parcel) => void;
  currentPage?: number;
  totalOfPage?: number;
  statusKey?: string;
  handlerAddNewParcel(newParcel: Parcel, orderUpdated: Order): void;
  handlerDeleteOrder: any;
  handlerUpdate: (updated: Order, changed: boolean, close: any, orderUpdated: Order) => void;
  page?: number;
  countTotal?: number;
  isSelected?: boolean;
  handleSearch?(searchText: string): void;
  setRowsPerPage?(length: number): void;
  rowsPerPage?: number;
  state?: any;
  handleChangePage?(event: unknown, newPage: number): void;
  refetchParcels: () => void;
  refetchOrders: () => void;
  rowsPerPageOptions?: number[];
  rowColor?:string
}

export function ParcelTrackingTable({
  actionSwitcher,
  parcels,
  switcher,
  ordersData,
  onClickOrder,
  onClickParcel,
  refetchParcels,
  refetchOrders,
  statusKey,
  handlerAddNewParcel,
  handlerDeleteOrder,
  handlerUpdate,
  handleSearch,
  page,
  countTotal,
  isSelected,
  currentPage,
  handleChangePage,
  state,
  setRowsPerPage,
  rowsPerPage,
  rowsPerPageOptions,
  rowColor
}: ParcelTrackingListProps) {
  const styles = useParcelStyles({color: rowColor});
  const { text } = useText();
  const { client } = useCurrentClient();
  const { user } = useCurrentUser();
  const { data, loading } = parcels;
  const { orders, orderLoading } = ordersData;
  const [checked, check] = React.useState(false);
  const [idParcelToDelete, setIdParcelToDelete] = React.useState<string>("");
  const [idOrderToDelete, setIdOrderToDelete] = React.useState<String>("");
  const orderActions = useOrderActions(text);
  const parcelActions = useParcelActions(text);
  const [dataParcelToUpdate, setDataParcelToUpdate] = React.useState<Parcel>(null);
  const [parcelsToAirBills, setParcelsToAirBills] = React.useState<Array<Parcel>>([]);
  const [recapParcels, { data: recapData }] = useLazyQuery(RecapParcels);
  const [openDelete, setOpenDelete] = React.useState(false);
  const [openUpdateOrder, setOpenUpdateOrder] = React.useState(false);
  const [openAddParcel, setOpenAddParcel] = React.useState(false);
  const [openChangeReceiver, setOpenChangeReceiver] = React.useState(false);
  const { selectedDate } = React.useContext(dateContext);
  const { job } = React.useContext(JobContext);
  const countryCode = useCountryCode();
  const dispatchAlert = useAlertDispatch();
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const tabsParcels = useTabs(
    recapData?.recapParcels?.data?.inprogress.count,
    recapData?.recapParcels?.data?.successful.count,
    recapData?.recapParcels?.data?.cancelled.count
  );

  const tabsOrders = useTabs(
    state.orders.onprogress?.length,
    state.orders.successful?.length,
    state.orders.cancelled?.length
  );

  React.useEffect(() => {
    if (client)
      selectedDate.from === null
        ? recapParcels({
            variables: {
              params: { clientID: client?._id },
            },
          })
        : recapParcels({
            variables: {
              params: { clientID: client?._id },
              queries: { from_date: selectedDate.from, to_date: selectedDate.to },
            },
          });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, client]);

  const onChangePage = (event: React.ChangeEvent<{}>, newValue: number) => {
    handleChangePage(event, newValue);
  };

  const ParcelRowColumns = useParcelRowColumns();
  const OrderColums = useOrdersColums();

  const [openDeleteOrderDialog, setOpenDeleteOrderDialog] = React.useState(false);
  const [openDeleteParcelDialog, setOpenDeleteParcelDialog] = React.useState(false);

  const [openUpdateDialog, setOpenUpdateDialog] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    check(event.target.checked);
    switcher(event.target.checked);
  };

  const addAirbillList = (parcel: Parcel) => {
    let list = [...parcelsToAirBills];
    const index = list?.findIndex((element) => element._id === parcel._id);
    if (index === -1) {
      list.push(parcel);
    } else {
      list = list?.filter((element) => element._id.localeCompare(parcel._id) !== 0);
    }
    setParcelsToAirBills(list);
  };

  const [row, setRow] = React.useState(null);
  const updateOrderHandler = (row: any) => {
    if (row) {
      setRow(row);
      setOpenUpdateOrder(true);
    }
  };
  const changeReceiver = (order: Order) => {
    if (order) {
      setRow(order);
      setOpenChangeReceiver(true);
    }
  };
  const handleAddParcelDialog = (row: Order) => {
    setOpenAddParcel(true);
    setRow(row);
  };
  const deleteOrderHandler = (row: any) => {
    const cantBeDeleted =
      row.order_status !== OrderStatus.ToPick ||
      row.order_status !== OrderStatus.ToPrepare ||
      row.order_status !== OrderStatus.ToReceive;
    if (row && cantBeDeleted) {
      setOpenDeleteOrderDialog(true);
      setIdOrderToDelete(row?._id);
    }
  };
  const onRowMenuClick = (key: string, row: any) => {
    switch (key) {
      case "edit":
        return updateOrderHandler(row);
      case "change":
        return changeReceiver(row);
      case "add":
        return handleAddParcelDialog(row);
      case "delete":
        return deleteOrderHandler(row);
      // no default
    }
  };
  const updateParcelHandler = (row: any) => {
    setOpenUpdateDialog(true);
    setDataParcelToUpdate(row);
  };
  const deleteParcelHandler = (row: any) => {
    setOpenDeleteParcelDialog(true);
    setIdParcelToDelete(row._id);
  };
  const onRowMenuParcelClick = (key: string, row: any) => {
    switch (key) {
      case "edit":
        return updateParcelHandler(row);
      case "delete":
        return deleteParcelHandler(row);
      // no default
    }
  };
  const updateReceiver = (item: any) => {
    let order: Order;
    if (Array.isArray(item)) {
      order = { order_receiver: item[0] };
    } else {
      order = { order_receiver: item };
    }
    handlerUpdate(order, true, setOpenChangeReceiver, row);
  };
  const handleAirwayBills = () => {
    if (parcelsToAirBills.length > 0) {
      generateAirwayBills(parcelsToAirBills, job, client, countryCode).download(
        `${moment().format("llll")}way-bills.pdf`
      );
    } else {
      dispatchAlert({
        severity: "warning",
        message: "Please select parcels to generate",
      });
    }
  };
  const handleExport = () => {
    setOpenDialog(true);
  };
  const actions = [
    {
      label: text.all.export_parcel,
      key: "1",
    },
    {
      label: text.all.airway_bills,
      key: "2",
    },
  ];
  const handleActions = (action: string) => {
    switch (action) {
      case "1":
        handleExport();
        break;
      case "2":
        handleAirwayBills();
        break;
      // no default
    }
  };
  const [searchTerm, setSearchTerm] = React.useState("");
  const handleSearchTerm = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(event.target.value.toLowerCase());
    setSearchTerm(event.target.value.toLowerCase());
  }, []);
  const smallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <div className={styles.rootParcel}>
      <HeaderParcelTracking statusKey={statusKey} checked={checked}>
        <SearchBox onChange={handleSearchTerm} value={searchTerm} className={styles.search} />
      </HeaderParcelTracking>
      <div className={styles.center}>
        <Grid container direction="row" className={styles.navcontainer}>
          <Grid className={styles.nav}>
            <NavSwitcher
              withBadge
              onChange={(tab) => {
                actionSwitcher(tab);
              }}
              tabs={checked ? tabsOrders : tabsParcels}
            />
          </Grid>
          <Grid className={styles.ant}>
            <div className={styles.switch}>
              {text.all.parcels}
              <AntSwitch size="small" checked={checked} onChange={handleChange} />
              {text.all.orders}
            </div>
            <div>{!checked && <ActionsGroup color={COLORS.orange} onClick={handleActions} actions={actions} />}</div>
          </Grid>
        </Grid>
      </div>
      <div className={styles.table}>
        {!checked && (
          <Table
            tableHeight={smallScreen ? "43vh" : "62vh"}
            rowsIdentifier="parcel_UID"
            columns={ParcelRowColumns}
            data={data}
            hasSelection
            isLoading={data?.length === 0}
            onRowClick={onClickParcel}
            onRowChecked={addAirbillList}
            onAllRowChecked={() => {
              if (parcelsToAirBills.length !== data.length) {
                setParcelsToAirBills(data);
              } else {
                setParcelsToAirBills([]);
              }
            }}
            isShowMenu={statusKey === "onprogress"}
            multipleActions={parcelActions}
            onRowMenuClick={onRowMenuParcelClick}
            defaultOrder="parcel_last_edited_at"
            rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
            onChangePage={onChangePage}
            rowsPerPage={rowsPerPage}
            {...{ currentPage }}
            {...{ countTotal }}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        {checked && (
          <Table
            tableHeight={smallScreen ? "45vh" : "62vh"}
            rowsIdentifier="order_UID"
            isSelected={isSelected}
            searchTerm={searchTerm}
            columns={OrderColums}
            data={orders}
            hasSelection
            isShowMenu={statusKey === "onprogress"}
            isLoading={orderLoading}
            onRowClick={onClickOrder}
            multipleActions={orderActions}
            onRowMenuClick={onRowMenuClick}
            rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
            onChangePage={onChangePage}
            rowsPerPage={rowsPerPage}
            {...{ currentPage }}
            {...{ countTotal }}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </div>

      <DeleteDialog
        title={`${text.all.order} ${text.all.delete}`}
        message={`${text.all.are_sure} ${text.all.delete.toLowerCase()} this ${text.all.order}?`}
        open={openDelete}
        handlerCancel={() => {
          setOpenDelete(false);
        }}
        handlerConfirm={() => {
          setOpenDelete(false);
          handlerDeleteOrder(idOrderToDelete);
        }}
      />
      <UpdateReceiver
        open={openUpdateOrder}
        order={row}
        handleClose={() => setOpenUpdateOrder(false)}
        handlerUpdate={handlerUpdate}
        refetch={refetchOrders}
      />
      <NewReceiver
        open={openChangeReceiver}
        clientId={user?.company_user_clientId}
        handleClose={() => setOpenChangeReceiver(false)}
        key="receiver-modal"
      />
      <NewParcel
        open={openAddParcel}
        order={row}
        handleClose={() => setOpenAddParcel(false)}
        handleNew={handlerAddNewParcel}
      />
      <ParcelsExportFormModal
        clientId={client && client?._id}
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
      />
      <DeleteParcel
        id={idParcelToDelete}
        isOpenDialog={openDeleteParcelDialog}
        CloseDialog={() => {
          setOpenDeleteParcelDialog(false);
        }}
        refetch={refetchParcels}
      />
      <UpdateParcel
        open={openUpdateDialog}
        parcel={dataParcelToUpdate}
        handleClose={() => setOpenUpdateDialog(false)}
        refetch={refetchParcels}
      />
      <DeleteOrder
        id={idOrderToDelete}
        isOpenDialog={openDeleteOrderDialog}
        CloseDialog={() => {
          setOpenDeleteOrderDialog(false);
        }}
        refetch={refetchOrders}
      />
    </div>
  );
}
